import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, toast } from '@node-space/ui-components'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { REWARDS_REFERENCE_PREFIX } from 'pages/Rewards/constants/const'
import { useRewardsSheetContext } from 'pages/Rewards/context/RewardsSheetProvider'
import { useRewardsWithdraw } from 'pages/Rewards/hooks/api/useRewardsWithdraw'
import useRewardsTracking from 'pages/Rewards/hooks/useRewardsTracking'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'
import { generateReference } from 'utils/utils'
import { RewardsWithdrawFormData } from '../../types'
import { RewardsAmountInput } from './RewardsAmountInput'
import { RewardsCurrencySelect } from './RewardsCurrencySelect'
import { RewardsWalletSelect } from './RewardsWalletSelect'
import { getWithdrawValidationSchema } from './validationSchema'

export type WithdrawMode = 'standard' | 'removeAllocation'

interface RewardsWithdrawFormProps {
  withdrawType: WithdrawMode
}

export const RewardsWithdraw: React.FC<RewardsWithdrawFormProps> = ({ withdrawType }) => {
  const { t } = useTranslation()
  const { requestError, setRequestError } = useMappedErrorMessage()
  const { trackFormInitiate, trackFormCancel } = useRewardsTracking()
  const reactQueryClient = useQueryClient()
  const {
    rewardsSheetAction,
    selectedCurrency: portfolioCurrency,
    userPortfolios,
    rewardsProducts,
  } = useRewardsSheetContext()

  const isRemoveAllocation = withdrawType === 'removeAllocation'

  const formMethods = useForm<RewardsWithdrawFormData>({
    resolver: yupResolver(getWithdrawValidationSchema(t)),
    mode: 'onChange',
    defaultValues: {
      currency: portfolioCurrency,
      withdrawToWallet: null,
      amount: '',
    },
  })
  const { handleSubmit, setValue, watch } = formMethods
  const selectedCurrency = watch('currency')
  const selectedWallet = watch('withdrawToWallet')

  const [maxAmount, setMaxAmount] = useState(0)

  const { onRewardsWithdraw, isRewardsWithdrawPending } = useRewardsWithdraw({
    isRemoveAllocation,
    onSuccess: data => {
      reactQueryClient.invalidateQueries({
        queryKey: reactQueryKeys.rewardsPortfolios(API_PAGINATION_OFFSET, API_PAGINATION_LIMIT),
      })
      rewardsSheetAction?.onClose?.()
      toast({
        variant: 'success',
        header: t(
          isRemoveAllocation
            ? 'rewards.removeAllocation.successNotificationTitle'
            : 'rewards.withdraw.successNotificationTitle',
          {
            walletName: selectedWallet?.name || '',
            currency: `${data.amount} ${data.currency}`,
          }
        ),
      })
    },
    onError: error => {
      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage: isRemoveAllocation
          ? t('rewards.errors.removeAllocation')
          : t('rewards.errors.withdrawFromPortfolio'),
      })
    },
  })

  useEffect(() => {
    trackFormInitiate(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
  }, [isRemoveAllocation])

  useEffect(() => {
    if (selectedCurrency) {
      setValue('withdrawToWallet', null)
      setValue('amount', '')
    }
  }, [selectedCurrency, setValue])

  useEffect(() => {
    if (rewardsProducts.length === 1) {
      setMaxAmount(Number(userPortfolios[0]?.balance?.available) || 0)
    }
  }, [rewardsProducts])

  const onCancel = () => {
    trackFormCancel(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
    rewardsSheetAction?.onClose?.()
  }

  const onSubmit = (data: RewardsWithdrawFormData) => {
    setRequestError(null)

    const portfolio = userPortfolios.find(portfolio => portfolio.currency === selectedCurrency)

    if (!portfolio) {
      setRequestError({
        show: true,
        errorCode: 400,
        handledMessage: t('rewards.withdraw.portfolioNotFound'),
      })
      return
    }

    onRewardsWithdraw({
      reference: generateReference(REWARDS_REFERENCE_PREFIX),
      portfolioId: portfolio.id,
      counterparty: {
        walletId: data.withdrawToWallet?.value,
        amount: data.amount,
      },
    })
  }

  const setMaxAmountFromPortfolio = (value: number) => {
    setMaxAmount(value)
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        tag="form"
        flexCol
        gapY={16}
        width="full"
        paddingY={24}
        onSubmit={handleSubmit(onSubmit)}
      >
        {requestError?.show && (
          <Callout variant="error" testId="withdraw-error-callout">
            {requestError?.handledMessage}
          </Callout>
        )}

        {rewardsProducts.length > 1 && (
          <RewardsCurrencySelect
            selectedCurrency={selectedCurrency}
            disabled={!!portfolioCurrency || isRewardsWithdrawPending}
            filterByBalance={isRemoveAllocation ? 'blocked' : undefined}
            setMaxAmount={setMaxAmountFromPortfolio}
          />
        )}

        <RewardsWalletSelect
          selectedCurrency={selectedCurrency}
          name="withdrawToWallet"
          label={t('rewards.withdraw.withdrawToWallet')}
          disabled={!selectedCurrency || isRewardsWithdrawPending}
          testId="withdraw-to-wallet-combobox"
        />

        <RewardsAmountInput
          selectedCurrency={selectedCurrency}
          maxAmount={maxAmount}
          disabled={!selectedCurrency || isRewardsWithdrawPending}
          label={isRemoveAllocation ? t('rewards.removeAllocation.amountToRemove') : t('amount')}
        />

        <Box flex justify="between" gapX={12} paddingT={24}>
          <Button onClick={onCancel} variant="secondary" type="button" testId="cancel-button">
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            testId="withdraw-button"
            disabled={isRewardsWithdrawPending}
            loading={isRewardsWithdrawPending}
          >
            {t('rewards.withdrawCurrency', {
              currency: selectedCurrency,
            })}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
